/* eslint-disable no-classname/no-classname-outside-designsystem */
import { type AssetVizProps } from '@asset-viz/defines'
import { t } from '@lingui/core/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { cn } from 'designSystem/lib/utils'
import { ForecastAssetViz } from 'features/assetVizAdapter/ForecastAssetViz'
import { MeasurementStrip } from 'features/assetVizAdapter/MeasurementStrip'
import { GenericRangeSelector } from 'features/dataFilter'
import { ChevronDown, Maximize2 } from 'lucide-react'
import * as React from 'react'

import { AssetVizToolbarToggle } from './AssetVizToolbarToggle'
import { AssetVizTopToolbar } from './AssetVizTopToolbar'
import type { AssetVizResolutionOption } from './resolutionOptions'
import type { ForecastAssetVizWindowState } from './useForecastAssetVizWindowState'

interface ForecastAssetVizWindowSurfaceProps extends AssetVizProps {
  className?: string
  children?: React.ReactNode
  windowState: ForecastAssetVizWindowState
  showViewModeToggle: boolean
  showResolutionSelector: boolean
  resolutionOptions: readonly AssetVizResolutionOption[]
  selectedResolutionId: string
  selectedResolutionLabel: string
  onSelectResolution: (cdfId: string) => void
  showMeasurementStrip: boolean
  showColorScaleSettings: boolean
  topLeftContent?: React.ReactNode
  topCenterContent?: React.ReactNode
  onExpand?: () => void
}

export const ForecastAssetVizWindowSurface = React.forwardRef<
  React.ElementRef<typeof ForecastAssetViz>,
  ForecastAssetVizWindowSurfaceProps
>(function ForecastAssetVizWindowSurface(
  {
    className,
    children,
    componentDataFrames,
    sceneConfig,
    windowState,
    showViewModeToggle,
    showResolutionSelector,
    resolutionOptions,
    selectedResolutionId,
    selectedResolutionLabel,
    onSelectResolution,
    showMeasurementStrip,
    showColorScaleSettings,
    topLeftContent,
    topCenterContent,
    onExpand,
    ...assetVizProps
  },
  ref
) {
  const settingsContent =
    showColorScaleSettings && windowState.hasRangeControls ? (
      <GenericRangeSelector
        cdfId={windowState.selectedCDF!.id}
        selectedComponentDefaults={undefined}
        measurementUnit={windowState.selectedCDF!.data.params.measurement_unit}
        selectedColorScale={windowState.selectedColorScale}
        fullValueRange={windowState.statsData!.value_range}
        filteredValueRange={windowState.filteredValueRange}
        onFilteredValueRangeChange={windowState.setFilteredValueRange}
        cdfStatsHistogram={windowState.statsData!.histogram}
        selectedColorThreshold={windowState.selectedColorThreshold!}
        minThresholdColor={windowState.selectedColorScale.getColorForValue(
          windowState.selectedColorThreshold!.min.num
        )}
        midThresholdColor={windowState.selectedColorScale.getColorForValue(
          windowState.selectedColorThreshold!.mid.num
        )}
        maxThresholdColor={windowState.selectedColorScale.getColorForValue(
          windowState.selectedColorThreshold!.max.num
        )}
        onColorThresholdChanged={windowState.setSelectedColorThreshold}
        compact
      />
    ) : undefined
  const hasResolutionSelector =
    showResolutionSelector && resolutionOptions.length > 1
  const hasTopToolbar =
    !!topLeftContent ||
    !!topCenterContent ||
    showViewModeToggle ||
    hasResolutionSelector
  return (
    <div className={cn('relative overflow-hidden bg-black', className)}>
      <ForecastAssetViz
        ref={ref}
        {...assetVizProps}
        componentDataFrames={componentDataFrames}
        sceneConfig={{
          ...sceneConfig,
          show3D: windowState.effectiveShow3D,
        }}
        onHovered={windowState.onHovered}
        getColorThresholdForComponent={
          windowState.getColorThresholdForComponent
        }
        cdfToFilteredValueRange={windowState.cdfToFilteredValueRange}
        onSceneConfigOptionsChanged={windowState.onSceneConfigOptionsChanged}
        className='size-full min-h-0'>
        {children}
      </ForecastAssetViz>

      {hasTopToolbar ? (
        <AssetVizTopToolbar
          leadingContent={
            <>
              {topLeftContent}
              {hasResolutionSelector ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      aria-label={t`Resolution`}
                      className='h-6 gap-0.5 border border-border/70 bg-background/80 px-1.5 text-[0.625rem] font-normal text-muted-foreground shadow-sm backdrop-blur-sm hover:border-border hover:bg-muted hover:text-foreground aria-expanded:border-border aria-expanded:bg-muted aria-expanded:text-foreground'>
                      <span className='max-w-24 truncate'>
                        {selectedResolutionLabel}
                      </span>
                      <ChevronDown
                        className='size-3 shrink-0 opacity-70'
                        aria-hidden
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='start' className='w-40 p-1'>
                    <DropdownMenuRadioGroup
                      value={selectedResolutionId}
                      onValueChange={onSelectResolution}>
                      {resolutionOptions.map((resolutionOption) => (
                        <DropdownMenuRadioItem
                          key={resolutionOption.cdf.id}
                          value={resolutionOption.cdf.id}
                          className='text-xs'>
                          {resolutionOption.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </>
          }
          centerContent={topCenterContent}
          trailingContent={
            <>
              {showViewModeToggle ? (
                <AssetVizToolbarToggle
                  ariaLabel={t`View mode`}
                  value={windowState.effectiveShow3D}
                  onChange={windowState.setShow3D}
                  options={[
                    { value: false, label: '2D' },
                    {
                      value: true,
                      label: '3D',
                      disabled: !windowState.threeDimensionsEnabled,
                    },
                  ]}
                />
              ) : null}
            </>
          }
        />
      ) : null}

      {showMeasurementStrip ? (
        <MeasurementStrip
          lo={windowState.lo}
          hi={windowState.hi}
          loColor={windowState.loColor}
          hiColor={windowState.hiColor}
          cursorValue={windowState.cursorValue}
          cursorColor={windowState.cursorColor}
          cursorLocation={windowState.hoveredCoords}
          settingsContent={settingsContent}
          endContent={
            onExpand ? (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                aria-label={t`Expand asset viewer`}
                onClick={onExpand}
                className='pointer-events-auto border border-border/70 bg-background/80 text-muted-foreground shadow-sm backdrop-blur-sm hover:bg-muted hover:text-foreground'>
                <Maximize2 className='size-3.5' aria-hidden />
              </Button>
            ) : undefined
          }
          backgroundClassName='bg-transparent'
          className='pointer-events-none absolute inset-x-0 bottom-0'
        />
      ) : null}
    </div>
  )
})
